import { cleanPermalink } from './top-videos';
import type { ReportParams, ReportRow } from './export-account-report-sheets';
import { downloadDocx, type DocxFile } from './open-docx-in-gdocs';

/**
 * Word-выгрузка «ссылка + тема», которую в медиа до сих пор собирали руками.
 *
 * Ручной путь был кружным: сайт отдавал xlsx, скрипт xlsx_to_word/gen.py его
 * распаковывал как zip, доставал картинки из xl/media, конвертировал WebP через
 * sips и складывал два docx. Все данные для этих файлов у нас уже есть в тех же
 * строках отчёта, поэтому xlsx как промежуточный формат больше не нужен.
 *
 * Формат повторяет gen.py, чтобы файлы не отличались от привычных:
 * Calibri 11 без отбивок, на публикацию — ссылка синим подчёркиванием, под ней
 * тема, а в варианте со скринами ещё и кадр. Между публикациями пустая строка.
 */

/** Цвет гиперссылки из gen.py — стандартный синий Word. */
const LINK_COLOR = '0563C1';
/**
 * Рамка для скрина в px. Ровно та же, что в xlsx-выгрузке: оттуда картинки и
 * попадали в докс, а 200 px по ширине — это те самые 1905000 EMU, которые
 * gen.py подставлял по умолчанию.
 */
const IMAGE_BOX = { width: 200, height: 280 };

export type LinksDocxEntry = {
  permalink: string;
  theme: string;
  imageUrl: string;
};

/**
 * Строки отчёта → записи документа. Пустые пропускаем: gen.py так же
 * пропускал строку, где нет ни ссылки, ни описания.
 */
export function buildLinksEntries(rows: readonly ReportRow[]): LinksDocxEntry[] {
  return rows
    .map((row) => ({
      permalink: cleanPermalink((row.permalink ?? '').trim()),
      theme: (row.theme ?? '').trim(),
      imageUrl: row.imageUrl ?? '',
    }))
    .filter((e) => e.permalink || e.theme);
}

/** Имена двух файлов — как их называл gen.py. */
export function linksDocxFilenames(title: string): { plain: string; withShots: string } {
  const safe = title.replace(/[\\/:*?"<>|]/g, '_').trim() || 'report';
  return { plain: `${safe}.docx`, withShots: `${safe} (со скринами).docx` };
}

/** Вписывает картинку в рамку, сохраняя пропорции. Не увеличивает. */
export function fitImageBox(
  naturalWidth: number,
  naturalHeight: number,
  box = IMAGE_BOX,
): { width: number; height: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0) return { ...box };
  const scale = Math.min(box.width / naturalWidth, box.height / naturalHeight, 1);
  return { width: Math.max(1, Math.round(naturalWidth * scale)), height: Math.max(1, Math.round(naturalHeight * scale)) };
}

export type LinksDocxImage = { data: Uint8Array; type: 'jpg' | 'png' | 'gif' | 'bmp'; width: number; height: number };
type FetchedImage = LinksDocxImage;

async function fetchImage(url: string): Promise<FetchedImage | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = new Uint8Array(await blob.arrayBuffer());
    const type: FetchedImage['type'] = blob.type.includes('png')
      ? 'png'
      : blob.type.includes('gif')
        ? 'gif'
        : blob.type.includes('bmp')
          ? 'bmp'
          : 'jpg';

    const bitmap = await createImageBitmap(blob).catch(() => null);
    const fitted = fitImageBox(bitmap?.width ?? IMAGE_BOX.width, bitmap?.height ?? IMAGE_BOX.height);
    bitmap?.close();
    return { data, type, ...fitted };
  } catch {
    // Картинка не загрузилась — публикация всё равно попадёт в файл, без кадра.
    return null;
  }
}

/**
 * Собирает документ. Вынесено из generateLinksDocx, чтобы сборку можно было
 * прогнать без браузера и сверить результат с файлами, которые делал gen.py.
 */
export async function buildLinksDocument(
  entries: readonly LinksDocxEntry[],
  images: readonly (LinksDocxImage | null)[],
  withShots: boolean,
) {
  const { Document, Paragraph, TextRun, ExternalHyperlink, ImageRun } = await import('docx');

  // Calibri 11 без отбивок сверху и снизу — стиль Normal из gen.py.
  const styles = {
    default: {
      document: {
        run: { font: 'Calibri', size: 22 },
        paragraph: { spacing: { before: 0, after: 0 } },
      },
    },
  };

  const children = entries.flatMap((entry, i) => {
    const block = [];
    // Пустая строка-разделитель между публикациями, но не перед первой.
    if (i > 0) block.push(new Paragraph({ children: [] }));

    block.push(
      new Paragraph({
        children: entry.permalink
          ? [
              new ExternalHyperlink({
                link: entry.permalink,
                children: [new TextRun({ text: entry.permalink, color: LINK_COLOR, underline: {} })],
              }),
            ]
          : [],
      }),
    );
    block.push(new Paragraph({ children: [new TextRun({ text: entry.theme })] }));

    const img = withShots ? images[i] : null;
    if (img) {
      block.push(
        new Paragraph({
          spacing: { before: 120 }, // 6 pt, как в gen.py
          children: [
            new ImageRun({
              data: img.data,
              type: img.type,
              transformation: { width: img.width, height: img.height },
            }),
          ],
        }),
      );
    }
    return block;
  });

  return new Document({ styles, sections: [{ children }] });
}

/** Два файла: без скринов и со скринами — порядок тот же, что у скачивания. */
export async function buildLinksDocxFiles(params: ReportParams): Promise<DocxFile[]> {
  const entries = buildLinksEntries(params.rows);
  if (entries.length === 0) throw new Error('Нет данных для экспорта');

  const images = await Promise.all(
    entries.map((e) => (e.imageUrl ? fetchImage(e.imageUrl) : Promise.resolve(null))),
  );

  const names = linksDocxFilenames(`${params.accountTitle} ${params.period}`.trim());
  const { Packer } = await import('docx');

  return [
    { blob: await Packer.toBlob(await buildLinksDocument(entries, images, false)), filename: names.plain },
    { blob: await Packer.toBlob(await buildLinksDocument(entries, images, true)), filename: names.withShots },
  ];
}

export async function generateLinksDocx(params: ReportParams): Promise<void> {
  const [plain, withShots] = await buildLinksDocxFiles(params);
  downloadDocx(plain);
  // Второй файл отдаём чуть позже: два скачивания подряд браузер может
  // посчитать за одно и отдать только первый.
  await new Promise((r) => setTimeout(r, 400));
  downloadDocx(withShots);
}
